<template>
  <div class="main-canvas">
    <template v-if="isReady">
      <div ref="scrollCanvas" class="scroll-canvas">
        <TopBar
          :key="`TopBar-${key}-${currentLocale}`"
          :selected.sync="selected"
          @changeDuration="onChangeSelected"
          @changeView="onChangeView"
          @search="onSearch"
          @close="onClose"
        />
        <MainHeader
          :key="`MainHeader-${key}-${currentLocale}`"
          :selected.sync="selected"
          @changeDuration="onChangeSelected"
          @changeView="onChangeView"
          @search="onSearch"
          @change="onChangeSelected"
        />
        <MainContent
          :key="`MainContent-${key}-${currentLocale}`"
          :is-loading="isLoading"
          :selected.sync="selected"
          :events="events"
          :event-categories="eventCategories"
          @change="onChangeSelected"
          @close="onClose"
        />
        <CalendarCategories
          :key="`CalendarCategories-${key}-${currentLocale}`"
          :selected.sync="selected"
          :events="events"
          :event-categories="eventCategories"
          @change="onChangeSelected"
        />

        <MainFooter
          :key="`MainFooter-${key}-${currentLocale}`"
        />
      </div>
      <Overlay
        :key="`Overlay-${key}-${currentLocale}`"
        :event="selectedEvent"
      />
    </template>
    <LoadingOverlay v-else :key="`LoadingOverlay-${key}-${currentLocale}`" />
  </div>
</template>

<script>
import moment from 'moment'
import _ from 'lodash'
import { v4 as uuidv4 } from 'uuid'

import EventService from '@/services/EventService'
import OverlayService from '@/services/OverlayService'
import GlobalDataService from '@/services/GlobalDataService'
import TranslateService from '@/services/TranslateService'
import MainHeader from '@/components/MainHeader.vue'
import MainContent from '@/components/MainContent.vue'
import MainFooter from '@/components/MainFooter.vue'
import CalendarCategories from '@/components/CalendarCategories'
import Overlay from '@/components/Overlay'
import LoadingOverlay from '@/components/LoadingOverlay'
import TopBar from '@/components/TopBar'

export default {
  components: {
    TopBar,
    LoadingOverlay,
    MainHeader,
    MainContent,
    MainFooter,
    Overlay,
    CalendarCategories
  },
  data () {
    return {
      currentLocale: null,
      selected: {
        date: moment().format('YYYY-MM-DD'),
        // date: moment('2024-03-08').format('YYYY-MM-DD'),
        duration: 'day',
        view: 'grid'
      },
      selectedEvent: null,
      events: [],
      eventCategories: [],
      isReady: false,
      isLoading: false,
      key: uuidv4()
    }
  },
  async mounted () {
    await GlobalDataService.init()
    await TranslateService.init()
    await this.updateEventCategories()

    _.set(this.selected, 'category', _.first(this.eventCategories))

    // for debug
    // this.selected.event = this.events[6]
    // // console.log(this.selected.event.location, this.selected.event.category)
    // console.log(555, this.selected.event)

    this.currentLocale = TranslateService.currentLocale

    TranslateService.subscribe('change', (locale, isUpdateUrl) => {
      if (isUpdateUrl && this.currentLocale !== locale) {
        this.updatePageUrl()
      }
      this.currentLocale = TranslateService.currentLocale
    })
    OverlayService.subscrib('event', (event) => {
      this.selectedEvent = event
      this.updatePageUrl()
    })
    OverlayService.subscrib('loading', (isLoading) => {
      this.isLoading = isLoading
    })

    const locale = _.get(this.$route, 'query.locale')
    if (locale) {
      TranslateService.setCurrentLocale(locale, true)
    }
    const selected = _.pick(this.$route.query, ['date', 'duration', 'view'])
    if (!_.isEmpty(selected)) {
      this.selected = _.extend(this.selected, selected)
    }
    await this.updateEvents()

    const eventId = _.get(this.$route, 'query.eventId')
    if (eventId) {
      this.selectedEvent = _.find(this.events, { id: eventId })
    }

    this.isReady = true
  },
  methods: {
    updatePageUrl () {
      const query = {
        locale: TranslateService.currentLocale,
        ..._.pick(this.selected, ['date', 'duration', 'view'])
      }
      if (this.selectedEvent) {
        query.eventId = this.selectedEvent.id
      }
      this.$router.push({ query })
    },
    async updateEvents () {
      OverlayService.startLoading()
      this.events = []
      const date = _.get(this.selected, 'date')
      let events
      if (this.selected.duration === 'day') {
        events = await EventService.getEvents({
          date_from: { $lte: date },
          date_to: { $gte: date }
        }, { orderBy: 'date_from' })
      } else {
        let weekStart = moment(date).startOf('week')
        let weekEnd = moment(weekStart).add(6, 'd')
        weekStart = weekStart.format('YYYY-MM-DD')
        weekEnd = weekEnd.format('YYYY-MM-DD')
        events = await EventService.getEvents({
          date_from: { $lte: weekEnd },
          date_to: { $gte: weekStart }
        }, { orderBy: 'date_from' })
      }

      const locations = await EventService.getLocations({ loc_id: { $in: _.uniq(_.map(events, 'locId')) } })
      const departments = await EventService.getDepartments({ dept_id: { $in: _.uniq(_.map(events, 'deptId')) } })
      const speakers = await EventService.getSpeakers({ e_id: { $in: _.uniq(_.map(events, 'id')) } })
      const organisers = await EventService.getOrganisers({ e_id: { $in: _.uniq(_.map(events, 'id')) } })
      const campuses = await EventService.getCampuses({ c_id: { $in: _.uniq(_.map(locations, 'cId')) } })
      _.each(events, event => {
        event.location = _.find(locations, { locId: event.locId })
        event.department = _.find(departments, { deptId: event.deptId })
        event.category = _.find(this.eventCategories, { catId: event.catId })
        event.speakers = _.filter(speakers, { eId: event.id })
        event.organisers = _.filter(organisers, { eId: event.id })
        if (event.location) {
          event.location.campus = _.find(campuses, { cId: event.location.cId })
        }
      })
      if (this.selected.category && this.selected.category.catId) {
        events = _.filter(events, { category: this.selected.category })
      }
      _.set(this.selected, 'isSearch', false)
      this.events = events
      // console.log(111, 'this.events', this.events)
      OverlayService.stopLoading()
      if (this.isReady) {
        this.updatePageUrl()
      }
    },
    async updateEventCategories () {
      let eventCategories = await EventService.getEventCategories()
      eventCategories = _.sortBy(eventCategories, category => category.displayOrder)
      eventCategories = [
        {
          catId: null,
          name: 'T_ALL_CATEGORIES',
          bgColor: '#A9A9A9'
        },
        ...eventCategories
      ]
      this.eventCategories = eventCategories
      // console.log(222, 'this.eventCategories', this.eventCategories)
    },
    async onChangeSelected () {
      await this.updateEvents()
      this.key = uuidv4()
    },
    async onChangeView () {
      this.key = uuidv4()
    },
    async onClose () {
      await this.updateEvents()
      this.key = uuidv4()
    },
    async onSearch (searchText) {
      OverlayService.startLoading()
      this.events = []
      const date = moment().format('YYYY-MM-DD')
      const events = await EventService.getEvents({
        $and: [
          {
            $or: [
              {
                title_en: { $like: `%${searchText}%` }
              },
              {
                title_tc: { $like: `%${searchText}%` }
              },
              {
                title_sc: { $like: `%${searchText}%` }
              }
            ]
          },
          {
            $or: [
              {
                date_from: { $lte: date },
                date_to: { $gte: date }
              },
              {
                date_from: { $gte: date }
              }
            ]
          }
        ]
      }, { orderBy: 'date_from' })

      const locations = await EventService.getLocations({ loc_id: { $in: _.uniq(_.map(events, 'locId')) } })
      const departments = await EventService.getDepartments({ dept_id: { $in: _.uniq(_.map(events, 'deptId')) } })
      const speakers = await EventService.getSpeakers({ e_id: { $in: _.uniq(_.map(events, 'id')) } })
      const organisers = await EventService.getOrganisers({ e_id: { $in: _.uniq(_.map(events, 'id')) } })
      const campuses = await EventService.getCampuses({ c_id: { $in: _.uniq(_.map(locations, 'cId')) } })
      _.each(events, event => {
        event.location = _.find(locations, { locId: event.locId })
        event.department = _.find(departments, { deptId: event.deptId })
        event.category = _.find(this.eventCategories, { catId: event.catId })
        event.speakers = _.filter(speakers, { eId: event.id })
        event.organisers = _.filter(organisers, { eId: event.id })
        if (event.location) {
          event.location.campus = _.find(campuses, { cId: event.location.cId })
        }
      })
      // _.set(this.selected, 'duration', 'week')
      _.set(this.selected, 'isSearch', true)
      this.events = events

      OverlayService.stopLoading()
      this.key = uuidv4()
    }
  }
}
</script>

<style lang="scss">
@import '@/_reset.scss';
@import '@/_fonts.scss';
@import '@/_calendar.scss';
@import '@/_variables.scss';

body {
  width: 100%;
  min-height: 100vh;
  height: 100vh;
  overflow: hidden;
  font-family: Poppins, arial;
  color: #333333;
  background-image: linear-gradient(90deg, #0c0080 65%, #275cf2 );
}

.main-canvas {
  height: 100vh;
  overflow: hidden;
  .scroll-canvas {
    display: fixed;
    height: 100vh;
    overflow: auto;
    position: relative;
  }
}

</style>
